import { useRef } from 'react'

import { useAuth, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import {
  Modal,
  PanelCloseProvider,
  usePanelClose,
  UserMenu as MoleculeUserMenu,
} from '@molecule/app-ui-react'

import { isGuestUser } from '../utilities/guestClaim.js'
import { SettingsPanel } from './SettingsPanel.js'

function UserMenuPanel() {
  return <SettingsPanel onClose={usePanelClose()} />
}

/**
 * The full user settings panel in the same drawer chrome the shared UserMenu
 * uses — for hosts with their OWN trigger button (the IDE toolbar's user icon),
 * so the user button opens the identical panel everywhere.
 */
export function UserSettingsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const cm = getClassMap()
  const { t } = useTranslation()
  return (
    <Modal
      open={open}
      onClose={onClose}
      className={cm.drawer}
      aria-label={t('userMenu.panelLabel', {}, { defaultValue: 'Account menu' })}
    >
      <PanelCloseProvider close={onClose}>
        <SettingsPanel onClose={onClose} />
      </PanelCloseProvider>
    </Modal>
  )
}

export function UserMenu() {
  const { user } = useAuth()
  const authLinkRef = useRef<HTMLAnchorElement>(null)

  // Guests see the auth modal (or signup page) instead of an empty settings panel.
  // An authenticated session ALWAYS wins: `user` comes from the real (cookie-
  // restored) session, while `mol_guest_uid` is only a claim hint — a stale one
  // (failed/skipped claim) must not lock a logged-in user out of their settings.
  // isGuestUser also catches an anonymous user row that reached client state
  // (e.g. hydrated via a stale mol_auth hint) — never show it the settings panel.
  const isGuest = isGuestUser(user)

  if (isGuest) {
    return (
      <>
        <a ref={authLinkRef} href="/signup" aria-hidden="true" style={{ display: 'none' }} />
        <MoleculeUserMenu onClick={() => authLinkRef.current?.click()}>
          <></>
        </MoleculeUserMenu>
      </>
    )
  }

  return (
    <MoleculeUserMenu>
      <UserMenuPanel />
    </MoleculeUserMenu>
  )
}
